import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import PageHeader from "@/components/base/PageHeader";
import type { NewsItem } from "@/types";
import { news as rawNews } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams();
  const news = rawNews as unknown as NewsItem[];
  const item = news.find((n) => n.id === id);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  useDocumentMeta({
    title: item ? `${item.title} - 舒淇动态详情` : "资讯详情 - 舒淇动态",
    description: item ? item.summary : "舒淇最新动态资讯详情。",
    keywords: item ? `舒淇动态,${item.title},舒淇资讯` : "舒淇动态",
  });

  const related = useMemo(() => {
    if (!item) return [];
    return news
      .filter((n) => n.id !== item.id)
      .sort((a, b) => {
        const aMatch = a.category === item.category ? 0 : 1;
        const bMatch = b.category === item.category ? 0 : 1;
        if (aMatch !== bMatch) return aMatch - bMatch;
        return a.date < b.date ? 1 : -1;
      })
      .slice(0, 3);
  }, [news, item]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2000);
  };

  if (!item) {
    return (
      <div>
        <PageHeader
          title="未找到该资讯"
          breadcrumb={[{ label: "首页", path: "/" }, { label: "新闻资讯", path: "/news" }]}
        />
        <div className="max-w-content mx-auto px-4 md:px-8 py-24 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-secondary-400">
            <i className="ri-error-warning-line text-3xl" />
          </div>
          <p className="mt-5 text-foreground-600">抱歉，这篇资讯可能已被移除。</p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow={item.category}
        title={item.title}
        breadcrumb={[
          { label: "首页", path: "/" },
          { label: "新闻资讯", path: "/news" },
          { label: "资讯详情" },
        ]}
      />

      <article className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-sm text-secondary-500">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs">
                {item.category}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {item.date}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-accent-400 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
          </div>

          <div className="mt-8 w-full aspect-[8/5] rounded-lg overflow-hidden bg-background-200">
            <img
              src={item.cover}
              alt={`${item.title} 舒淇动态资讯配图`}
              title={`${item.title} 舒淇最新动态`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {item.content.map((paragraph) => (
              <p
                key={paragraph.slice(0, 12)}
                className="text-sm md:text-base text-foreground-700 leading-loose"
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-background-200 flex items-center justify-between gap-4">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回资讯列表
            </Link>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 text-sm text-secondary-500 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-line" />
              分享这篇资讯
            </button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-background-100 border-t border-background-200">
          <div className="max-w-content mx-auto px-4 md:px-8 py-14 md:py-20">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
              相关文章
            </h2>
            <span className="block h-px w-12 bg-accent-400 mt-4 mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/news/${r.id}`}
                  className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-accent-300 transition-colors"
                >
                  <div className="w-full aspect-[8/5] overflow-hidden bg-background-200">
                    <img
                      src={r.cover}
                      alt={`${r.title} 舒淇相关资讯`}
                      title={`${r.title} 舒淇动态`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <p className="text-xs text-secondary-500">{r.date}</p>
                    <h3 className="mt-2 font-heading text-base font-semibold text-foreground-950 line-clamp-2 group-hover:text-accent-600 transition-colors">
                      {r.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {shareOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setShareOpen(false)}
          />
          <div className="relative w-full max-w-sm bg-background-50 rounded-lg p-6 animate-fade-up">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-foreground-950">
                分享这篇资讯
              </h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setShareOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <p className="mt-2 text-xs text-secondary-500">
              以下分享功能仅作演示，不会真实发送。
            </p>
            <div className="mt-6 grid grid-cols-4 gap-3">
              {[
                { icon: "ri-wechat-line", label: "微信" },
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-link", label: "复制链接" },
                { icon: "ri-more-line", label: "更多" },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => {
                    setShareOpen(false);
                    showToast(`${opt.label}分享（演示）`);
                  }}
                  className="flex flex-col items-center gap-2 py-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${opt.icon} text-xl`} />
                  </span>
                  <span className="text-xs text-foreground-700">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[90] animate-fade-up">
          <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm">
            <i className="ri-checkbox-circle-line text-accent-400 text-lg" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}